"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Truck, ShieldCheck, Clock } from "lucide-react";

const products = [
  "Patate douce",
  "Pomme de terre",
  "Manioc",
  "Igname",
  "Farine de manioc",
  "Chips de patate",
  "Attiéké",
  "Commande multiple / Gros",
];

export function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    product: "",
    quantity: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate async send
    await new Promise((r) => setTimeout(r, 1100));
    setLoading(false);
    toast.success("Commande envoyée !", {
      description: "Notre équipe vous recontacte sous 24h.",
    });
    setForm({ name: "", phone: "", email: "", product: "", quantity: "", message: "" });
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-earth-deep via-earth-deep to-earth" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-30"
             style={{ background: "radial-gradient(circle, oklch(0.86 0.13 80 / 0.5), transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-25"
             style={{ background: "radial-gradient(circle, oklch(0.55 0.13 135 / 0.4), transparent 70%)" }} />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: info — cream card with black text for max legibility */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative p-6 sm:p-10 rounded-3xl bg-cream shadow-gold-lg overflow-hidden">
              {/* Decorative gold corner accent */}
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-50"
                   style={{ background: "radial-gradient(circle, oklch(0.86 0.13 80 / 0.5), transparent 70%)" }} />

              <div className="relative">
                <span className="inline-block px-3 py-1 rounded-full text-xs uppercase tracking-[0.28em] font-bold text-earth-deep bg-gradient-gold shadow-gold">
                  Vente Directe
                </span>

                <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-earth-deep">
                  Commandez en{" "}
                  <span className="text-gradient-gold">direct</span>
                </h2>

                <p className="mt-6 text-foreground leading-relaxed text-base sm:text-lg">
                  Particulier, restaurateur, revendeur ou collectivité&nbsp;: passez votre
                  commande directement auprès de notre maison. Pas d&apos;intermédiaire, le
                  meilleur rapport qualité-prix, et la fierté d&apos;acheter au producteur.
                </p>

                <div className="mt-10 space-y-4">
                  <a href="tel:+22500000000" className="flex items-center gap-4 group">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-secondary group-hover:bg-gradient-gold transition-colors shadow-sm">
                      <Phone className="w-5 h-5 text-gold-deep group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gold-deep font-bold">Téléphone</div>
                      <div className="text-foreground font-semibold">+225 00 00 00 00 00</div>
                    </div>
                  </a>

                  <a href="mailto:contact@patate-or.ligue-islamique.online" className="flex items-center gap-4 group">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-secondary group-hover:bg-gradient-gold transition-colors shadow-sm">
                      <Mail className="w-5 h-5 text-gold-deep group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gold-deep font-bold">Email</div>
                      <div className="text-foreground font-semibold break-all">contact@patate-or.ligue-islamique.online</div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 group">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-secondary group-hover:bg-gradient-gold transition-colors shadow-sm">
                      <MapPin className="w-5 h-5 text-gold-deep group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gold-deep font-bold">Cultures & Usine</div>
                      <div className="text-foreground font-semibold">Ndiamsyl Peye, Smartest Capital</div>
                    </div>
                  </div>
                </div>

                {/* Trust badges */}
                <div className="mt-10 grid grid-cols-3 gap-3">
                  {[
                    { icon: Truck, label: "Livraison rapide" },
                    { icon: ShieldCheck, label: "Qualité certifiée" },
                    { icon: Clock, label: "24h délai réponse" },
                  ].map((t, i) => (
                    <div key={i} className="p-3 rounded-xl bg-secondary border border-gold/30 text-center">
                      <t.icon className="w-5 h-5 text-gold-deep mx-auto" />
                      <div className="mt-2 text-[11px] text-foreground font-bold">{t.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div className="relative p-6 sm:p-10 rounded-3xl bg-cream shadow-gold-lg">
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-gradient-gold grid place-items-center shadow-gold rotate-6">
                <Send className="w-8 h-8 text-white" />
              </div>

              <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                Passez votre commande
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Remplissez le formulaire ci-dessous. Notre équipe vous recontacte sous 24h
                avec une proposition de prix et de livraison.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-foreground/80">
                      Nom complet <span className="text-gold-deep">*</span>
                    </Label>
                    <Input
                      id="name"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Votre nom"
                      className="rounded-xl border-border focus-visible:ring-gold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground/80">
                      Téléphone <span className="text-gold-deep">*</span>
                    </Label>
                    <Input
                      id="phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+225 ..."
                      className="rounded-xl border-border focus-visible:ring-gold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground/80">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="vous@exemple.com"
                    className="rounded-xl border-border focus-visible:ring-gold"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="product" className="text-foreground/80">
                      Produit souhaité <span className="text-gold-deep">*</span>
                    </Label>
                    <Select
                      value={form.product}
                      onValueChange={(v) => setForm({ ...form, product: v })}
                    >
                      <SelectTrigger id="product" className="rounded-xl border-border focus-visible:ring-gold">
                        <SelectValue placeholder="Choisir un produit" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-foreground/80">
                      Quantité
                    </Label>
                    <Input
                      id="quantity"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      placeholder="ex : 25 kg"
                      className="rounded-xl border-border focus-visible:ring-gold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground/80">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Précisez votre demande : délai, lieu de livraison, conditionnement..."
                    className="rounded-xl border-border focus-visible:ring-gold resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="w-full bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg hover:scale-[1.01] transition-all rounded-xl font-semibold"
                >
                  {loading ? "Envoi en cours..." : "Envoyer ma commande"}
                  {!loading && <Send className="w-4 h-4 ml-2" />}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  En soumettant ce formulaire, vous acceptez d&apos;être recontacté par
                  PATATE OR concernant votre commande.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
